// WCAG contrast helpers, shared by anything that overlays branding-chosen
// text on a branding-chosen background.
//
// Previously duplicated inline in features/admin/branding/BrandingSettingsTab.tsx
// (which uses it to warn an admin picking `nav_text_color` against `navy_color`
// in the Branding tab's own preview). Pulled out here so a second, non-admin
// consumer — components that render accent-colored text on the navy sidebar/
// header bar (App.tsx's tagline/role labels, VisitStepper's Visit ID/progress
// text) — can apply the SAME check at render time, not just warn about it in
// a settings form nobody may have visited. A hospital that picks a low-
// contrast accent color (e.g. a pink against a similarly mid-toned navy)
// should not end up with genuinely unreadable text anywhere it's used.

/** "#RRGGBB" or "RRGGBB" -> relative luminance per the WCAG 2.x formula. */
export function relativeLuminance(hex: string): number {
  const m = /^#?([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i.exec(hex || '');
  if (!m) return 0;
  const [r, g, b] = [m[1]!, m[2]!, m[3]!].map((h) => {
    const c = parseInt(h, 16) / 255;
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  }) as [number, number, number];
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/** WCAG contrast ratio between two colors, always >= 1 regardless of argument order. */
export function contrastRatio(hexA: string, hexB: string): number {
  const L1 = relativeLuminance(hexA);
  const L2 = relativeLuminance(hexB);
  const [lighter, darker] = L1 >= L2 ? [L1, L2] : [L2, L1];
  return (lighter + 0.05) / (darker + 0.05);
}

/**
 * Picks which of `preferred` or `fallback` reads better against `bg`,
 * preferring `preferred` (the branding accent color, chosen for visual
 * identity) whenever it clears `minRatio` — WCAG AA's 4.5:1 for normal-size
 * text, the same "Good" threshold BrandingSettingsTab's own preview already
 * uses. Falls back to `fallback` only when the accent genuinely can't be
 * read there, rather than silently accepting whatever a hospital happened to
 * pick as their brand accent, no matter how it reads against navy.
 *
 * `fallback` is meant to be `nav_text_color` — itself already a
 * contrast-checked-against-navy color per the Branding tab — so the
 * degraded case still looks intentional rather than like a generic reset
 * to plain white.
 */
export function pickReadableAccent(bg: string, preferred: string, fallback: string, minRatio = 4.5): string {
  return contrastRatio(preferred, bg) >= minRatio ? preferred : fallback;
}

/**
 * Scales every RGB channel of `hex` by `factor` (0–1). Scaling all three
 * channels by the same factor leaves their ratios — and so the hue — exactly
 * as they were; only the HSV "value" (how dark it reads) drops. That's the
 * one property `ensureDark` below depends on.
 */
function scaleHex(hex: string, factor: number): string {
  const m = /^#?([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i.exec(hex || '');
  if (!m) return hex;
  const scaled = [m[1]!, m[2]!, m[3]!].map((h) => {
    const v = Math.round(parseInt(h, 16) * factor);
    return Math.max(0, Math.min(255, v)).toString(16).padStart(2, '0');
  });
  return scaled.join('');
}

/**
 * Darkens `hex` just enough to read as a genuinely dark surface — used for
 * chrome (a permanently-visible bar, a sidebar) that needs to look like
 * fixed UI regardless of how a hospital's own branding is set up. A
 * hospital's `navy_color` is chosen for visual identity, not guaranteed to
 * be dark — nothing stops it being picked as a bright teal or a pastel — so
 * using it verbatim for chrome like this can end up looking like a bug
 * (light background, white text) rather than a themed dark bar. Falling
 * back to a fixed neutral instead (e.g. plain slate) fixes the darkness but
 * throws the hospital's own color identity away entirely.
 *
 * This keeps the middle ground: repeatedly scale the color toward black
 * (`scaleHex`, hue-preserving) until its WCAG luminance drops under
 * `maxLuminance`. A hospital that already picked a proper dark navy gets it
 * back completely unchanged (already under the threshold, loop doesn't
 * run); one that picked something bright gets the *same hue*, just deep
 * enough to read as dark chrome — branded, not generic.
 */
export function ensureDark(hex: string, maxLuminance = 0.12): string {
  let current = hex.replace(/^#/, '');
  let factor = 1;
  for (let i = 0; i < 24 && relativeLuminance(current) > maxLuminance; i++) {
    factor *= 0.85;
    current = scaleHex(hex, factor);
  }
  return current;
}
